import { useEffect } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from './gsap';

/**
 * Declarative scroll reveals powered by GSAP ScrollTrigger.
 *
 * Mark any page-level element and it animates the first time it scrolls into view:
 *   <section data-reveal>                 fade + rise (default)
 *   <div data-reveal="fade">              fade only
 *   <div data-reveal="scale">             fade + subtle scale-up
 *   <ul data-reveal="stagger">            children cascade in one after another
 *     <li data-reveal-item>…</li>         (or, without data-reveal-item, direct children)
 *
 * One manager (mounted once in Root) handles every screen, including content that
 * arrives later from the network, via a MutationObserver. Elements revealed in the same
 * frame are staggered so a page settles in as a sequence rather than a jump.
 *
 * Only use on content in the main page scroll — modals and inner scroll areas animate
 * with Motion instead. With "reduce motion" enabled nothing is hidden or animated.
 */
const BOUND = 'revealBound';
const READY_CLASS = 'mtt-reveal-ready';

const FROM: Record<string, gsap.TweenVars> = {
  up: { autoAlpha: 0, y: 28 },
  fade: { autoAlpha: 0 },
  scale: { autoAlpha: 0, scale: 0.96, y: 12 },
};

function staggerChildren(el: HTMLElement): HTMLElement[] {
  const explicit = el.querySelectorAll<HTMLElement>('[data-reveal-item]');
  return explicit.length ? Array.from(explicit) : (Array.from(el.children) as HTMLElement[]);
}

export function ScrollReveals() {
  useEffect(() => {
    if (prefersReducedMotion()) return;

    const root = document.documentElement;
    root.classList.add(READY_CLASS);

    let queue: HTMLElement[] = [];
    let flushScheduled = false;

    const reveal = () => {
      flushScheduled = false;
      const batch = queue;
      queue = [];
      batch.forEach((el, index) => {
        const variant = el.dataset.reveal || 'up';
        const delay = Math.min(index * 0.07, 0.35);

        if (variant === 'stagger') {
          gsap.set(el, { autoAlpha: 1 });
          gsap.fromTo(
            staggerChildren(el),
            { autoAlpha: 0, y: 20 },
            { autoAlpha: 1, y: 0, duration: 0.6, delay, stagger: { each: 0.05, amount: 0.5 }, clearProps: 'transform' },
          );
          return;
        }
        gsap.fromTo(el, FROM[variant] ?? FROM.up, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          delay,
          clearProps: 'transform',
        });
      });
    };

    const enqueue = (el: HTMLElement) => {
      queue.push(el);
      if (!flushScheduled) {
        flushScheduled = true;
        requestAnimationFrame(reveal);
      }
    };

    const bind = (el: HTMLElement) => {
      if (el.dataset[BOUND]) return;
      el.dataset[BOUND] = '1';
      ScrollTrigger.create({
        trigger: el,
        start: 'top 92%',
        once: true,
        onEnter: () => enqueue(el),
      });
    };

    const scan = (node: ParentNode) => {
      if (node instanceof HTMLElement && node.hasAttribute('data-reveal')) bind(node);
      node.querySelectorAll?.<HTMLElement>('[data-reveal]').forEach(bind);
    };

    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        // Drop triggers whose elements left the DOM (route changes), then re-measure.
        ScrollTrigger.getAll().forEach(t => {
          const trigger = t.trigger as Element | undefined;
          if (trigger && !document.contains(trigger)) t.kill();
        });
        ScrollTrigger.refresh();
      }, 150);
    };

    scan(document.body);

    const observer = new MutationObserver(mutations => {
      let changed = false;
      for (const m of mutations) {
        m.addedNodes.forEach(n => {
          if (n.nodeType === 1) {
            scan(n as HTMLElement);
            changed = true;
          }
        });
        if (m.removedNodes.length) changed = true;
      }
      if (changed) scheduleRefresh();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Late layout shifts (images, fonts) move trigger positions.
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);
    document.fonts?.ready.then(onLoad).catch(() => {});

    return () => {
      observer.disconnect();
      clearTimeout(refreshTimer);
      window.removeEventListener('load', onLoad);
      root.classList.remove(READY_CLASS);
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return null;
}
